import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import {css, Theme, useTheme} from "@emotion/react";
import {Button} from "@pg-design/button";
import {elevation} from "@pg-design/elevation";
import {Col, Container, Row} from "@pg-design/grid";
import {
    borderRadius,
    calculateRemSize,
    flexAlignCenter,
    flexDirection,
    flexJustifyCenter,
    h100,
    mb,
    onDesktop,
    pb,
    ph,
    pt,
    pv
} from "@pg-design/helpers-css";
import {CheckIcon} from "@pg-design/icons";
import {Text} from "@pg-design/text";

import {chartsRoutes} from "../../../common/app/routing/charts_routes";
import {breakpoints} from "../../../common/app/styles/breakpoints";
import {LANDING_BIG_DATA_CONTACT_SECTION_ID} from "./hero/Hero";
import {MICRO_COPY_TEXT_VARIANT, microCopyAudienceList, microCopyAudienceListItem} from "./landingMicroCopy";

const scrollToContactForm = () => {
    document.getElementById(LANDING_BIG_DATA_CONTACT_SECTION_ID)?.scrollIntoView({behavior: "smooth"});
};

interface IPlanCardProps {
    title: string;
    subtitle: string;
    bullets: string[];
    ctaLabel: string;
    to?: string;
    onCtaClick?: () => void;
}

const PlanCard = ({title, subtitle, bullets, ctaLabel, to, onCtaClick}: IPlanCardProps) => {
    const theme = useTheme();

    const cta = (
        <Button variant="filled_primary" size="big" css={ctaButton} onClick={onCtaClick}>
            {ctaLabel}
        </Button>
    );

    return (
        <div css={planCard}>
            <div css={planCardTop}>
                <Text
                    strong
                    variant="body_copy_1"
                    align="center"
                    as="h3"
                    color={theme.colors.secondary}
                    css={planTitle}
                >
                    {title}
                </Text>
                <Text variant="body_copy_1" align="center" color={theme.colors.secondary} css={planCardSubtitle}>
                    {subtitle}
                </Text>
            </div>
            <hr css={divider(theme)} />
            <div css={planCardBottom}>
                <ul css={list}>
                    {bullets.map((line) => (
                        <li key={line} css={listItem}>
                            <CheckIcon
                                size="1.6"
                                wrapperSize="1.6"
                                wrapperColor={theme.colors.primary}
                                css={listIcon}
                            />
                            <Text variant="info_txt_1" as="span">
                                {line}
                            </Text>
                        </li>
                    ))}
                </ul>
                <div css={ctaWrap}>
                    {to != null ? (
                        <Link to={to} css={ctaLink}>
                            {cta}
                        </Link>
                    ) : (
                        <div css={ctaLink}>{cta}</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export const BigDataAccessPlans = () => {
    const {t, i18n} = useTranslation();
    const theme = useTheme();

    const audiences = useMemo(
        () => [
            t("landing.bigDataAccessPlans.audience1"),
            t("landing.bigDataAccessPlans.audience2"),
            t("landing.bigDataAccessPlans.audience3"),
            t("landing.bigDataAccessPlans.audience4")
        ],
        [t]
    );

    const demoBullets = useMemo(
        () => [
            t("landing.bigDataAccessPlans.demo.bullet1"),
            t("landing.bigDataAccessPlans.demo.bullet2"),
            t("landing.bigDataAccessPlans.demo.bullet3"),
            t("landing.bigDataAccessPlans.demo.bullet4")
        ],
        [t]
    );

    const fullBullets = useMemo(
        () => [
            t("landing.bigDataAccessPlans.full.bullet1"),
            t("landing.bigDataAccessPlans.full.bullet2"),
            t("landing.bigDataAccessPlans.full.bullet3"),
            t("landing.bigDataAccessPlans.full.bullet4"),
            t("landing.bigDataAccessPlans.full.bullet5")
        ],
        [t]
    );

    const routes = chartsRoutes[i18n.language] ?? chartsRoutes.pl;

    return (
        <Container as="section" fluid css={section(theme)}>
            <Container>
                <Row>
                    <Col lg={16} md={12} sm={4}>
                        <Text variant="headline_1" align="center" as="h2" css={mb(4)}>
                            {t("landing.bigDataAccessPlans.title")}
                        </Text>
                        <ul css={audienceList}>
                            {audiences.map((item) => (
                                <li key={item} css={audienceListItem}>
                                    <Text variant={MICRO_COPY_TEXT_VARIANT} as="span">
                                        {item}
                                    </Text>
                                </li>
                            ))}
                        </ul>
                    </Col>
                </Row>

                <Row css={plansRow}>
                    <Col lg={5} md={6} sm={4} css={colStretch}>
                        <PlanCard
                            title={t("landing.bigDataAccessPlans.demo.title")}
                            subtitle={t("landing.bigDataAccessPlans.demo.subtitle")}
                            bullets={demoBullets}
                            ctaLabel={t("landing.bigDataAccessPlans.demo.cta")}
                            onCtaClick={scrollToContactForm}
                        />
                    </Col>
                    <Col lg={5} md={6} sm={4} css={colStretch}>
                        <PlanCard
                            title={t("landing.bigDataAccessPlans.full.title")}
                            subtitle={t("landing.bigDataAccessPlans.full.subtitle")}
                            bullets={fullBullets}
                            ctaLabel={t("landing.bigDataAccessPlans.full.cta")}
                            to={routes.pricing}
                        />
                    </Col>
                </Row>
            </Container>
        </Container>
    );
};

const section = (theme: Theme) => css`
    ${pv(9)};
    background-color: ${theme.colors.gray[100]};
`;

const audienceList = css`
    ${microCopyAudienceList};
    ${mb(6)};
`;

const audienceListItem = microCopyAudienceListItem;

const plansRow = css`
    ${flexJustifyCenter};
    row-gap: ${calculateRemSize(3)};
    align-items: stretch;
`;

const colStretch = css`
    display: flex;
    min-width: 0;

    & > * {
        ${h100};
        flex: 1;
        min-width: 0;
    }
`;

const planTitle = css`
    ${mb(2)};
    text-transform: uppercase;
`;

const planCardSubtitle = css`
    display: block;
    min-height: ${calculateRemSize(4.5)};
    margin: 0;
`;

const planCard = css`
    padding: 0;
    background-color: #fff;
    ${borderRadius(2)};
    ${elevation(4)};
    ${flexDirection("column")};
    display: flex;
    align-items: stretch;
    width: 100%;
`;

const planCardTop = css`
    ${pt(3)};
    ${ph(3)};
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    min-height: ${calculateRemSize(9)};

    ${onDesktop(css`
        ${pt(4)};
        ${ph(4)};
        min-height: ${calculateRemSize(10)};
    `)}
`;

const planCardBottom = css`
    display: flex;
    ${flexDirection("column")};
    align-items: stretch;
    flex: 1 1 auto;
    min-height: 0;
    ${ph(3)};
    ${pb(3)};

    ${onDesktop(css`
        ${ph(4)};
        ${pb(4)};
    `)}
`;

const divider = (theme: Theme) => css`
    flex-shrink: 0;
    border: 0;
    border-top: 1px solid ${theme.colors.gray[200]};
    margin: 0 0 ${calculateRemSize(3)};
    width: 100%;
`;

const list = css`
    list-style: none;
    margin: 0 0 ${calculateRemSize(4)};
    padding: 0;
    flex: 1 1 auto;
`;

const listItem = css`
    display: flex;
    align-items: flex-start;
    gap: ${calculateRemSize(1.5)};
    ${mb(2)};

    &:last-of-type {
        ${mb(0)};
    }
`;

const listIcon = css`
    flex-shrink: 0;
    margin-top: 2px;
`;

const ctaWrap = css`
    margin-top: auto;
    ${flexAlignCenter};
    justify-content: center;
`;

const ctaLink = css`
    display: inline-block;
    text-decoration: none;
    width: 100%;
    max-width: 22rem;
`;

const ctaButton = css`
    width: 100%;
    text-transform: uppercase;
    font-weight: 700;
    border-radius: 9999px;

    @media (min-width: ${breakpoints.xxs}) {
        min-width: 20.8rem;
    }
`;
